import { Injectable, Injector } from '@angular/core'
import { Response, URLSearchParams } from '@angular/http'
import { Observable } from 'rxjs/Observable'
import * as _ from 'lodash'

import { APP_INJECTOR } from 'app.module'
import { AppHttp } from 'core/http'
import { Authentication } from 'core/auth'
import { KeyTransformer } from 'util/key-transformer'
import { Constants } from 'config'
import { CurrentUser } from 'models'

interface ISerializer{
  serialize<T>(obj: T|Object): Object;

  deserialize<T>(attributes: Object): T;
}

@Injectable()
export abstract class BaseModelService<T> {
  protected appHttp: AppHttp
  protected apiPath: string = Constants.API.PATH
  protected basePath: string
  protected key: string
  protected pluralKey: string
  protected serializer: ISerializer
  protected authentication: Authentication


  constructor() {
    this.appHttp = APP_INJECTOR.get(AppHttp)
    this.authentication = APP_INJECTOR.get(Authentication)
  }

  currentUser(): CurrentUser {
    return this.authentication.currentUser
  }

  buildUrl(...params: any[]): string {
    return [this.apiPath, this.basePath, ...params].join('/') + '.json'
  }

  configure(config: Object) {
    Object.assign(this, config)
  }

  get(url: string, params?: Object): Observable<Object> {
    params = Object.assign({}, params, {
      current_role: this.currentUser() ? this.currentUser().currentRole : "guest"
    })
    let searchParams = this.buildUrlSearchParams(params)
    return this.appHttp.get(url, { search: searchParams })
      .map((response) => this.processResponse(response))
  }

  post(url: string, obj?: T|Object, query?: Object): Observable<Object> {
    let body = null
    if(obj) {
      let params = {}
      params[this.objectKey()] = this.serialize<T>(obj)
      body = JSON.stringify(params)
    }
    query = Object.assign({}, query, {
      current_role: this.currentUser() ? this.currentUser().currentRole : "guest"
    })
    let queryParams = this.buildUrlSearchParams(query)

    return this.appHttp.post(url, body, {
        search: queryParams
      })
      .map((response) => this.processResponse(response))
  }

  patch(url: string, obj?: T|Object, query?: Object): Observable<Object> {
    let body = null
    if(obj) {
      let params = {}
      params[this.objectKey()] = this.serialize<T>(obj)
      body = JSON.stringify(params)
    }
    query = Object.assign({}, query, {
      current_role: this.currentUser() ? this.currentUser().currentRole : "guest"
    })
    let queryParams = this.buildUrlSearchParams(query)

    return this.appHttp.patch(url, body, {
        search: queryParams
      })
      .map((response) => this.processResponse(response))
  }

  delete(url: string, params?: Object): Observable<Object> {
    params = Object.assign({}, params, {
      current_role: this.currentUser() ? this.currentUser().currentRole : "guest"
    })
    let searchParams = new URLSearchParams()
    if(params) {
      params = KeyTransformer.underscore(params)
      for(let key in params) {
        searchParams.set(key, params[key])
      }
    }
    return this.appHttp.delete(url, { search: searchParams })
      .map((response) => this.processResponse(response))
  }

  index(params?: Object): Observable<Object> {
    let url = this.buildUrl()
    return this.get(url, params)
  }

  shortIndex(params?: Object): Observable<Object> {
    let url = this.buildUrl('short_index')
    return this.get(url, params)
  }

  find(id: number|string): Observable<Object> {
    let url = this.buildUrl(id)
    return this.get(url)
  }

  save(obj: T|Object): Observable<Object> {
    if(obj['id']) {
      return this.update(obj)
    } else {
      return this.create(obj)
    }
  }

  create(obj: T|Object): Observable<Object> {
    let url = this.buildUrl()
    return this.post(url, obj)
  }

  update(obj: T|Object): Observable<Object> {
    let url = this.buildUrl(obj['id'])
    return this.patch(url, obj)
  }

  destroy(obj: T, params?: Object): Observable<Object> {
    let url = this.buildUrl(obj['id'])
    return this.delete(url, params)
  }

  restore(obj: T): Observable<Object> {
    let url = this.buildUrl(obj['id'], 'restore')
    return this.patch(url)
  }

  disable(obj: T): Observable<Object> {
    let url = this.buildUrl(obj['id'], 'disable')
    return this.patch(url)
  }

  buildUrlSearchParams(rawParams: Object): URLSearchParams {
    let params = KeyTransformer.underscore(rawParams)
    let result = new URLSearchParams()
    this.appendUrlSearchParams(result, params)
    return result
  }

  protected processResponse(response: Response): Object {
    if(!response.text()) { return null }

    let rawData = response.json()
    if(!rawData) { return rawData }

    let data = KeyTransformer.camelize(rawData)
    let result = {}
    for (let k in data) {
      if (k == this.key) {
        result[k] = this.deserialize(data[k])
      }
      else if (k == this.pluralKey) {
        result[k] = data[k].map((attributes) => { return this.deserialize(attributes) })
      } else {
        result[k] = data[k]
      }
    }
    return result
  }

  protected serialize<T>(obj?: T|Object): Object {
    return this.serializer.serialize<T>(obj)
  }

  protected deserialize(attributes: Object): T {
    return this.serializer.deserialize<T>(attributes)
  }

  protected appendUrlSearchParams(target: URLSearchParams, params: Object, keyPrefix: string = null) {
    for(let key in params) {
      let pKey = keyPrefix ? `${keyPrefix}[${key}]` : key
      let value = params[key]
      if (_.isBoolean(value)){
        target.append(pKey, value)
      } else if (!value) {
        continue;
      } else if(_.isArray(value)) {
        if(value.length > 0) {
          target.append(pKey, value.join(','))
        } else {
          target.append(`${pKey}[]`, '')
        }
      } else if(_.isObject(value)) {
        this.appendUrlSearchParams(target, value, pKey)
      } else {
        target.append(pKey, value.toString())
      }
    }
  }

  protected objectKey(){
    return KeyTransformer.underscoreKey(this.key)
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./app/models/base-model.service.ts